/**
 * THE COOKIE POLICY, WRITTEN ONCE (PV5a) — prest0.ai/cookie-policy, in today's design (src/pages/Cookie.tsx) and in the
 * new design's reading page (src/design/pavilion/pages/cookie-policy/). See ./legalDocument.ts for how a document is
 * shared.
 *
 * Moved here from Cookie.tsx as written, part names aside; the page's rendered HTML was compared byte for byte before
 * and after the move. Today's page drew its section titles with no ids; the ids below are new, for the new design's
 * contents, and today's page still draws its titles without them. The date it took effect is
 * src/config/legalVersions.ts's (EffectiveDate).
 *
 * 2026-10-17: the two outside scripts the site does load are named (Stripe.js on the card and top-up forms, Google
 * Calendar's booking page in the demo-booking window), the server logs' retention and what they can hold, the storage
 * the app really keeps (no builder, dashboards or monetization tools exist), and Google Cloud's reports named as Google's.
 * The video players an insights post can load are named too (YouTube's privacy-enhanced player, Vimeo with
 * do-not-track, Loom: src/insights/insightsModel.ts embedPlayerUrl), each only once play is pressed (the facade in
 * InsightsRender.tsx loads nothing from the host before).
 */
import React from 'react';
import type { LegalDocument, LegalParts } from './legalDocument';

/** What the page says above the document: its title and the sentence under it. */
export const COOKIE_POLICY_HEAD = {
  heading: 'cookie policy',
  lead: 'how prest0 uses cookies and similar technologies to protect and personalize your experience',
  /** The contents the new design's reading page draws (today's page has none). */
  contentsTitle: 'on this page',
  contentsName: 'cookie policy table of contents',
} as const;

export type CookiePolicyParts = Pick<
  LegalParts,
  'Callout' | 'Cell' | 'Fact' | 'FactLabel' | 'FactValue' | 'Facts' | 'HeadCell' | 'List' | 'ListItem' | 'OrderedList' | 'OrderedListItem' | 'SubTitle' | 'Table' | 'Text'
>;

/** The cookie policy, in the parts the caller draws it with. */
export function cookiePolicyDocument({
  Callout, Cell, Fact, FactLabel, FactValue, Facts, HeadCell, List, ListItem, OrderedList, OrderedListItem, SubTitle, Table, Text,
}: CookiePolicyParts): LegalDocument {
  return {
    intro: (
      <>
        <Text>
          prest0 uses cookies and similar technologies to provide, maintain, and continually improve our services. This cookie policy explains what data we collect through these technologies, how we use it, and the options available to you.
        </Text>
        <Text>
          The way we set, store, and honor cookies lines up with the commitments on our security page: TLS protects every session, data at rest is encrypted with AES-256 on Google Cloud, sign-in through Google or an email and password guards account access, we are committed to prompts never training shared models (enforcement being built, targeted for October 31, 2026), and documented retention schedules govern storage lifecycles.
        </Text>

        <Facts>
          <Fact>
            <FactLabel>cookie scope</FactLabel>
            <FactValue>first-party storage on prest0.ai, plus the fraud-prevention cookies Stripe sets on the payment forms — we set no advertising or analytics cookies and load no third-party trackers</FactValue>
          </Fact>
          <Fact>
            <FactLabel>retention window</FactLabel>
            <FactValue>session-based, plus the sign-in token, the preferences your browser keeps for you, and a note of how you arrived, kept up to 90 days</FactValue>
          </Fact>
          <Fact>
            <FactLabel>consent banner</FactLabel>
            <FactValue>none — we set no advertising or analytics cookies; the one optional note, how you arrived, is never kept when your browser sends Global Privacy Control or Do Not Track</FactValue>
          </Fact>
        </Facts>

        <Callout>
          you will not meet a cookie wall here, and that is not an oversight. prest0 runs no advertising trackers and no analytics product at all, so there is no tracking cookie for a banner to ask you about. what we keep in your browser keeps you signed in, remembers how you have set things up and, until you create an account, notes how you first arrived (a link's campaign tags and the name of the site that sent you), never when your browser sends Global Privacy Control or Do Not Track. the payment forms load stripe, which sets its own fraud-prevention cookies. your browser controls remain the full set of controls.
        </Callout>
      </>
    ),
    sections: [
      {
        id: 'security-controls',
        heading: 'how cookies align with our security controls',
        body: (
          <>
            <Text>
              Our security page outlines the baseline controls we run across every workspace. Those same safeguards govern how cookies and similar technologies work inside prest0:
            </Text>
            <List>
              <ListItem>TLS keeps preference requests and authentication cookies encrypted in transit.</ListItem>
              <ListItem>Data stored in Google Cloud (including the preferences you set) is protected with AES-256 encryption and managed key rotation.</ListItem>
              <ListItem>Sign-in through Google or an email and password, and role-based permissions, guard access to admin tooling.</ListItem>
              <ListItem>Our no-training commitment covers anything tied to cookies too: nothing derived from them is to fine-tune a shared model.</ListItem>
              <ListItem>Server logs are kept for 30 days, and Google Cloud's admin-activity audit logs for 400 days. Self-service timers and automated deletion receipts are on the roadmap, not shipped.</ListItem>
              <ListItem>Append-only audit logs back the platform these preferences live on. It runs on Google Cloud, which holds SOC 2 Type II and ISO/IEC 27001 reports for its infrastructure; those reports are Google's, not prest0's.</ListItem>
            </List>
          </>
        ),
      },
      {
        id: 'what-are-cookies',
        heading: 'what are cookies?',
        body: (
          <>
            <Text>
              Cookies are small text files that websites place on your device to remember information about your visit. Some persist after you close your browser (persistent cookies) while others disappear when you end the session (session cookies). We rely on both types to keep the prest0 experience smooth, secure, and personalized.
            </Text>
          </>
        ),
      },
      {
        id: 'categories',
        heading: 'cookie categories we rely on',
        body: (
          <>
            <Text>
              We group cookies by how they support your workflow. The table below summarizes each category and how it shows up while you use prest0.
            </Text>

            <Table>
              <thead>
                <tr>
                  <HeadCell>category</HeadCell>
                  <HeadCell>how it supports your session</HeadCell>
                  <HeadCell>examples you may see</HeadCell>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <Cell>essential</Cell>
                  <Cell>keeps you signed in, protects gated areas, remembers fundamental preferences, and routes traffic securely across our infrastructure.</Cell>
                  <Cell>the sign-in token your browser keeps for you, and the fraud-prevention cookies Stripe sets while a card or top-up form is open.</Cell>
                </tr>
                <tr>
                  <Cell>performance & analytics</Cell>
                  <Cell>one first-party note and no analytics product: until you create an account, your browser keeps the campaign tags of the link you came by, the name of the site that sent you and the first page you landed on, for up to 90 days, and sends it only if you sign up. we run no Google Analytics, no Segment, no PostHog, no session replay and no A/B testing allocator.</Cell>
                  <Cell>the arrival note (prest0_arrival in local storage), never kept when your browser sends Global Privacy Control or Do Not Track.</Cell>
                </tr>
                <tr>
                  <Cell>functionality</Cell>
                  <Cell>remembers how you have set up the chat, the thread you had open and drafts you have not sent, and keeps a copy of recent threads so the page loads faster.</Cell>
                  <Cell>chat layout and width, the open thread, unsent drafts, your theme.</Cell>
                </tr>
                <tr>
                  <Cell>marketing</Cell>
                  <Cell>none. we set no advertising cookie and embed no social or ad-network pixel — no Meta, no LinkedIn, no X.</Cell>
                  <Cell>nothing is set in this category.</Cell>
                </tr>
              </tbody>
            </Table>
          </>
        ),
      },
      {
        id: 'technologies',
        heading: 'technologies we rely on',
        body: (
          <>
            <SubTitle>browser cookies</SubTitle>
            <Text>
              What prest0.ai keeps in your browser for sign-in, preferences and navigation is scoped to our own domain, and every exchange with our servers rides over TLS. The cookies set by others are Stripe's fraud-prevention cookies on the card and top-up forms, which are strictly necessary to take a payment safely, and Google's own, on Google's sign-in window when you choose to sign in with Google and on Google Calendar's booking page in the demo-booking window, each under Google's terms. A video in an insights post loads nothing from its host until you press play; then the host's player loads (YouTube in its privacy-enhanced mode, Vimeo with do-not-track, or Loom) and may set that host's own cookies, under its terms.
            </Text>
            <SubTitle>local storage & indexeddb</SubTitle>
            <Text>
              For real-time editing, drafts and layout we keep working state in local storage and IndexedDB, and before you have an account, the note of how you arrived. That data stays in your browser, is sent to our servers only over TLS, and is encrypted at rest on Google Cloud once it arrives. We do not encrypt it client-side before it leaves your browser, and we would rather say so than imply a protection we have not built.
            </Text>
            <SubTitle>server-side logging</SubTitle>
            <Text>
              Standard server logs capture IP address, browser metadata, and timestamps. Most entries record ids, counts and timings, but some can hold a phone number or part of a message. Those logs sit on Google Cloud, are encrypted at rest with AES-256, and are kept for 30 days.
            </Text>
          </>
        ),
      },
      {
        id: 'when-we-set-cookies',
        heading: 'when we set cookies',
        body: (
          <>
            <List>
              <ListItem>when you create or access your account to maintain a secure session.</ListItem>
              <ListItem>when you use the chat and your settings, to remember your layout and choices.</ListItem>
              <ListItem>when you open a card or top-up form: Stripe.js loads and sets Stripe's fraud-prevention cookies.</ListItem>
              <ListItem>when you open the demo-booking window, or visit prest0.ai/demo: Google Calendar's booking page loads and may set Google's own cookies. It loads only then.</ListItem>
              <ListItem>when you press play on a video in an insights post: its host's player loads (YouTube in its privacy-enhanced mode, Vimeo with do-not-track, or Loom) and may set that host's own cookies. Nothing loads from the host before you press play.</ListItem>
            </List>
          </>
        ),
      },
      {
        id: 'managing-preferences',
        heading: 'managing your preferences',
        body: (
          <>
            <Text>
              Most browsers allow you to control cookies through their settings. The exact location differs, but these steps cover the general flow:
            </Text>
            <OrderedList>
              <OrderedListItem>open your browser settings or preferences pane.</OrderedListItem>
              <OrderedListItem>look for privacy, security, or cookies controls.</OrderedListItem>
              <OrderedListItem>choose whether to block, clear, or allow cookies and site data.</OrderedListItem>
              <OrderedListItem>save your changes and refresh the prest0 tab.</OrderedListItem>
            </OrderedList>
            <Text>quick references for popular browsers:</Text>
            <List>
              <ListItem><strong>Google Chrome:</strong> Settings &gt; Privacy and security &gt; Cookies and other site data.</ListItem>
              <ListItem><strong>Safari:</strong> Settings &gt; Safari &gt; Privacy & Security or Preferences &gt; Privacy on macOS.</ListItem>
              <ListItem><strong>Microsoft Edge:</strong> Settings &gt; Cookies and site permissions &gt; Manage and delete cookies and site data.</ListItem>
              <ListItem><strong>Firefox:</strong> Settings &gt; Privacy & Security &gt; Cookies and site data.</ListItem>
            </List>
          </>
        ),
      },
      {
        id: 'analytics-and-third-parties',
        heading: 'analytics and third-party cookies',
        body: (
          <>
            <Text>
              There are no analytics or advertising cookies. prest0.ai loads no analytics product, no advertising network and no social pixel, so there is no tracking cookie for you to opt out of and no vendor holding a record of your visit. We do not sell or share personal information, and we are committed to nothing on this site feeding a shared AI model. On the public pages, the one thing we measure is how fast each page loaded, sent to our own server with no identifier and no cookie. The arrival note above stays in your browser and reaches our own server only when you create an account; no vendor ever receives it.
            </Text>
            <Text>
              The third-party requests the site makes are for the fonts it renders in (from Google Fonts and Fontshare), the Google Cloud infrastructure it is served from, Google's sign-in when you choose it, Stripe.js on the card and top-up forms, whose fraud-prevention cookies are strictly necessary to take a payment safely, Google Calendar's booking page in the demo-booking window, which Google serves under its own terms and which loads only when you open it, and a video's own host (YouTube, Vimeo or Loom) once you press play on a video in an insights post. If that ever changes, it changes here first, with a new effective date, before the code ships.
            </Text>
          </>
        ),
      },
      {
        id: 'retention',
        heading: 'retention and refresh cycles',
        body: (
          <>
            <Text>
              Session cookies last as long as your session. Your sign-in token lasts until it expires or you sign out. Preferences your browser holds for you — layout, the last thing you were working on, unsent drafts — stay until you clear your browser data. Stripe's and Google's cookies last as their own cookie policies say. The arrival note expires after 90 days, or is cleared once it has been sent with a new account. There are no analytics or marketing cookies to expire, because none are set.
            </Text>
          </>
        ),
      },
      {
        id: 'do-not-track',
        heading: 'do not track and global privacy control',
        body: (
          <>
            <Text>
              If your browser sends a Global Privacy Control or Do Not Track signal, we honor it: your browser keeps no arrival note, so an account made from it carries no record of how you arrived. Nothing else changes, because we were never selling or sharing your personal information, running cross-site advertising, or setting a tracker for anyone to read.
            </Text>
          </>
        ),
      },
      {
        id: 'policy-updates',
        heading: 'updates to this policy',
        body: (
          <>
            <Text>
              We review this policy at least annually and whenever we introduce significant product changes or new third-party partnerships. Updates become effective once they appear on this page with a refreshed date stamp.
            </Text>
          </>
        ),
      },
    ],
  };
}
